import { Mail, Phone, MapPin } from "lucide-react";
import { PageHero } from "@/components/ui/PageHero";
import { TextField, TextAreaField, SelectField } from "@/components/ui/form/FormField";
import { FormAlert } from "@/components/ui/form/FormAlert";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { analytics } from "@/lib/analytics";
import { contact } from "@/config/contact";
import { contactServiceOptions } from "@/data/form-options";

const seo = pageSeo(
  "Contact Us — Let's Discuss Your Infrastructure",
  "Tell us what you are building, what you currently use and where you need help. Our team will review your requirements and guide you toward the appropriate solution."
);

type ContactFormValues = {
  "first-name": string;
  "last-name": string;
  email: string;
  phone: string;
  company: string;
  service: string;
  infrastructure: string;
  requirements: string;
  message: string;
  consent: string;
};

function validate(values: ContactFormValues) {
  const errors: Partial<Record<keyof ContactFormValues, string>> = {};
  if (!values["first-name"]?.trim()) errors["first-name"] = "Please enter your first name.";
  if (!values["last-name"]?.trim()) errors["last-name"] = "Please enter your last name.";
  if (!values.email?.trim()) errors.email = "Please enter a business email.";
  else if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = "Enter a valid email address.";
  if (!values.message?.trim()) errors.message = "Please tell us a bit about what you need.";
  if (!values.consent) errors.consent = "Please confirm you're happy for us to contact you.";
  return errors;
}

export function ContactPage() {
  useDocumentMeta(seo.title, seo.description);
  const { status, errors, handleSubmit } = useFormSubmit<ContactFormValues>(
    validate,
    "contact_form_submit"
  );

  return (
    <>
      <PageHero
        eyebrow="Get in Touch"
        title="Let's Discuss Your Infrastructure"
        description="Tell us what you are building, what you currently use and where you need help. Our team will review your requirements and guide you toward the appropriate solution."
      />

      <section className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <a
            href={contact.emailHref}
            onClick={() => analytics.track("email_click")}
            className="focus-ring rounded-card border border-border bg-navy-card p-6 transition hover:border-cyan/40"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
              <Mail className="h-4 w-4" strokeWidth={1.6} />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-text-primary">Email</h3>
            <p className="mt-1 text-sm text-text-secondary">{contact.email}</p>
          </a>
          <a
            href={contact.phoneHref}
            onClick={() => analytics.track("phone_click")}
            className="focus-ring rounded-card border border-border bg-navy-card p-6 transition hover:border-cyan/40"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
              <Phone className="h-4 w-4" strokeWidth={1.6} />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-text-primary">Phone</h3>
            <p className="mt-1 text-sm text-text-secondary">{contact.phone}</p>
          </a>
          <div className="rounded-card border border-border bg-navy-card p-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
              <MapPin className="h-4 w-4" strokeWidth={1.6} />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-text-primary">Address</h3>
            <p className="mt-1 text-sm text-text-secondary">{contact.address.full}</p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="glass-panel mt-12 space-y-5 rounded-card p-6 sm:p-10"
          noValidate
        >
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          {status === "success" && <FormAlert status="success" />}
          {status === "error" && <FormAlert status="error" />}

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField label="First Name" name="first-name" required error={errors["first-name"]} />
            <TextField label="Last Name" name="last-name" required error={errors["last-name"]} />
            <TextField label="Business Email" name="email" type="email" required error={errors.email} />
            <TextField label="Phone Number" name="phone" type="tel" />
            <TextField label="Company Name" name="company" />
            <SelectField label="Service Required" name="service" defaultValue="">
              <option value="" disabled>Select a service</option>
              {contactServiceOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </SelectField>
          </div>

          <TextField label="Current Infrastructure" name="infrastructure" placeholder="What are you currently using, if anything?" />
          <TextField label="Estimated Requirements" name="requirements" placeholder="e.g. expected traffic, storage, number of mailboxes" />
          <TextAreaField label="Message" name="message" required error={errors.message} placeholder="Tell us what you're building and where you need help" />

          <div>
            <label className="flex items-start gap-3 text-sm text-text-secondary">
              <input
                type="checkbox"
                name="consent"
                value="yes"
                className="focus-ring mt-0.5 h-4 w-4 rounded border-border bg-navy-secondary text-cyan"
                aria-invalid={!!errors.consent}
                aria-describedby={errors.consent ? "consent-error" : undefined}
              />
              I agree to be contacted by The Planet about my enquiry.
            </label>
            {errors.consent && (
              <p id="consent-error" role="alert" className="mt-1.5 text-xs text-status-error">
                {errors.consent}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={status === "submitting"}
            className="focus-ring w-full rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Sending…" : "Send Message"}
          </button>
        </form>
      </section>
    </>
  );
}
